import React from "react";
import s from './setupFeedbackAndScoring.module.scss';
import {appPagePaths, appRoutePaths, miscImages} from "../../../config/config";
import {useNavigate} from "react-router-dom";
import BackButton from "../../common/BackButton";
import Button from "../../common/Button";
import {microphone} from "../../../hooks/microphone.hook";
import {displayErrorToUser} from "../../../services/error/error";
import {FontIcon} from "../../common/FontIcon";
import playgroundApi from "../../../services/playgroundApi";

export default function SetupFeedbackAndScoring() {
  const navigate = useNavigate();

  return (
    <div className={s.container}>
      <header className={s.top}>
        <BackButton useClassName={s.backButton}/>
        <picture>
          <source srcSet={miscImages.handsOnKeyboardAVIFLg} type={"image/avif"} media={"(orientation: landscape)"} />
          <source srcSet={miscImages.handsOnKeyboardAVIFSm} type={"image/avif"} media={"(orientation: portrait)"} />
          <source srcSet={miscImages.handsOnKeyboardWebPLg} type={"image/webp"} media={"(orientation: landscape)"} />
          <source srcSet={miscImages.handsOnKeyboardWebPSm} type={"image/webp"} media={"(orientation: portrait)"} />
          <source srcSet={miscImages.handsOnKeyboardPNGLg} type={"image/png"} media={"(orientation: landscape)"} />
          <source srcSet={miscImages.handsOnKeyboardPNGSm} type={"image/png"} media={"(orientation: portrait)"} />
          <img src={miscImages.handsOnKeyboardPNGLg} alt="Hands on a keyboard with a 95% accuracy score displayed" width={576} height={494} loading={"eager"} />
        </picture>
        <h1 className={s.overlayText}>
          Set up feedback<br/>and scoring
        </h1>
      </header>
      <main className={s.chooseFeedbackOption}>
        <h1 className={s.feedbackHeading}>Set up feedback and scoring</h1>
        <p className={s.pageInfo}>Choose how you want the app to provide scoring and progress tracking.</p>
        <div className={s.feedbackOptions}>
          {createButton({
            ariaLabel: "Use a USB or MIDI for keyboards",
            onClick: () => {
              void playgroundApi.recordOnboardingScoringMechanismChoice("MIDI");
              navigate(appPagePaths.setupYourKeyboardPage);
            },
            title: "Use a USB or MIDI cable for keyboards",
            listItems: [
              { text: "100% accurate scoring" },
              { text: "Super-precise real-time feedback and progress tracking" },
              { text: "Ideal for keyboards and digital pianos" }
            ],
            description: "Strongly recommended for keyboards & digital pianos",
            recommended: false,
            buttonLabel: "Use cable",
            buttonIcon: <FontIcon icon={"icon-cable-connector"} />
          })}

          {createButton({
            ariaLabel: "Use your device's microphone (for acoustic pianos)",
            onClick: () => {
              microphone.checkPermissions(false)
                .then((status) => {
                  playgroundApi.recordOnboardingScoringMechanismChoice("PITCH_DETECTION");

                  if (status === "granted") {
                    navigate(appRoutePaths.onboarding_setupFeedback_youAreAllSet);
                  } else if (status === "requestable") {
                    navigate(appRoutePaths.onboarding_setupFeedback_allowMicrophoneAccess);
                  } else {
                    navigate(appRoutePaths.onboarding_setupFeedback_micAccessNeeded);
                  }
                })
                .catch((error) => {
                  displayErrorToUser("An error occurred while checking microphone permissions.");
                  console.error("Error checking microphone permissions:", error);
                });
            },
            title: "Use your device's microphone (for acoustic pianos)",
            listItems: [
              { text: "Required for acoustic pianos" },
              { text: "Works with digital pianos or keyboards with speakers" },
              { text: "Good feedback accuracy for most setups" }
            ],
            recommended: false,
            buttonLabel: "Use microphone",
            buttonIcon: <FontIcon icon={"icon-microphone"} />,
          })}
        </div>
      </main>
    </div>
  );
}

interface IChoiceButtonProps {
  ariaLabel: string;
  onClick: () => void;
  title: string;
  listItems: { text: string, subText?: string }[];
  description?: string;
  buttonLabel: string;
  buttonIcon?: React.ReactElement;
  recommended: boolean;
}

function createButton({
  ariaLabel,
  onClick,
  title,
  listItems,
  description,
  buttonLabel,
  buttonIcon,
  recommended = false,
}: IChoiceButtonProps) {
  const accessibleLabel = ariaLabel ? ariaLabel : "";
  return <div className={s.inputMethodBody}>
    {recommended
      ? <div className={s.recommended}>{"Recommended"}</div>
      : null
    }
    <div className={s.inputMethodButtonBody}>
      <h2 className={s.buttonTitle}>{title}</h2>
      {description ? <div className={s.buttonDescription}>{description}</div> : null}
      <ul className={s.listItems}>
        {listItems.map((item, index) => {
          return <li className={s.listItem} key={index}>
            <div>
              {item.text}
              {
                item.subText ? <><br /><span className={s.itemSubText}>{item.subText}</span></> : null
              }
            </div>
          </li>;
        })}
      </ul>
      <div className={s.buttonContainer}>
        <Button
          icon={buttonIcon}
          text={buttonLabel}
          ariaLabel={accessibleLabel}
          buttonType={"secondary"}
          additionalClassName={s.inputMethodButton}
          onClick={onClick}
          minWidth={220}>
        </Button>
      </div>
    </div>
  </div>;
}
